import { useState } from "react";
import {
  useAddQueryToDashboard,
  useAddQueryToReport,
  useConnections,
  usePreviewTile,
  type Tile,
} from "@/api/queries";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { ShieldIcon } from "@/components/icons";
import { PresentationFields } from "./PresentationFields";
import { EMPTY_DRAFT, presentationFrom, type Draft } from "./presentation";

/**
 * "Add a tile" — a statement somebody typed, on a report or a dashboard
 * (PRD FR-5.5, FR-7.1, FR-7.3).
 *
 * The counterpart to `SaveAsReport`: a report or a dashboard does not have to
 * begin with an answer. Somebody who already knows the query writes it here
 * and the tile that appears refreshes, exports and schedules exactly like one
 * captured from a conversation — same guard, same audit, same vocabulary.
 *
 * **Validate, execute, materialise, then persist.** That order is the
 * server's (`ReportService.add_from_query`) and this dialog is deliberately
 * thin over it: there is no local parsing and no client-side idea of what is
 * safe. A refusal comes back as SQLGuard's own reason and is announced by the
 * mutation cache, and this dialog stays open with the statement still in it so
 * the reason can be acted on.
 */
export function AddTileDialog({
  open,
  onClose,
  owner,
  defaultConnectionId,
}: {
  open: boolean;
  onClose: () => void;
  owner: { kind: "report" | "dashboard"; id: string };
  /** The source the surface already describes, pre-selected. */
  defaultConnectionId?: string | null;
}) {
  const { data: connections } = useConnections();
  const toReport = useAddQueryToReport();
  const toDashboard = useAddQueryToDashboard();
  const add = owner.kind === "report" ? toReport : toDashboard;

  const [connectionId, setConnectionId] = useState("");
  const [sql, setSql] = useState("");
  const [title, setTitle] = useState("");
  // TILE-1: run it once to see its columns, then say how to draw it. The
  // preview is the server's materialisation, notes and all — nothing here
  // second-guesses whether a choice fits.
  const preview = usePreviewTile();
  const [previewed, setPreviewed] = useState<Tile | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  // Seed the source once per opening: pre-selected from the surface, or the
  // only source there is. Re-seeding on every render would fight the picker.
  const [seeded, setSeeded] = useState(false);
  if (open && !seeded && connections) {
    setSeeded(true);
    setConnectionId(defaultConnectionId || (connections.length === 1 ? connections[0]!.id : ""));
  }
  if (!open && seeded) {
    setSeeded(false);
    setPreviewed(null);
    setDraft(EMPTY_DRAFT);
  }

  function runPreview() {
    if (!connectionId || !sql.trim()) return;
    preview.mutate(
      {
        connection_id: connectionId,
        sql,
        title,
        presentation: draft.type === "table" ? null : presentationFrom(draft),
      },
      { onSuccess: setPreviewed },
    );
  }

  function submit() {
    if (!connectionId || !sql.trim()) return;
    const body = {
      connection_id: connectionId,
      sql,
      title,
      presentation: draft.type === "table" ? null : presentationFrom(draft),
    };
    const done = {
      onSuccess: () => {
        setSql("");
        setTitle("");
        setPreviewed(null);
        setDraft(EMPTY_DRAFT);
        onClose();
      },
    };
    // Two owners, two endpoints, one shape — and `mutate` rather than a bare
    // `mutateAsync`, so a refusal is the cache's to announce rather than an
    // unhandled rejection (backlog item 11).
    if (owner.kind === "report") {
      toReport.mutate({ reportId: owner.id, ...body }, done);
    } else {
      toDashboard.mutate({ dashboardId: owner.id, ...body }, done);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Add a tile"
      description="Write a statement and it becomes a tile — refreshable, exportable and schedulable like any other."
      footer={
        <>
          <span className="type-caption text-ink-disabled me-auto inline-flex items-center gap-1.5">
            <ShieldIcon className="size-3.5" />
            read-only, guarded and audited
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            loading={preview.isPending}
            disabled={!connectionId || !sql.trim()}
            onClick={runPreview}
          >
            Preview
          </Button>
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={!connectionId || !sql.trim()}
            onClick={submit}
          >
            Run and add
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Source"
          value={connectionId}
          onChange={(event) => setConnectionId(event.target.value)}
          hint={
            owner.kind === "dashboard"
              ? "A dashboard can gather tiles from several databases."
              : "A report keeps the source of its first tile in its header."
          }
        >
          <option value="">Pick a source…</option>
          {(connections ?? []).map((connection) => (
            <option key={connection.id} value={connection.id}>
              {connection.name} · {connection.engine}
            </option>
          ))}
        </Select>

        <label className="flex flex-col gap-1.5">
          <span className="type-label text-ink">Title</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Defaults to “Query result”"
            className="bg-surface border-border rounded-element control-lg type-body text-ink focus:border-border-emphasized motion-fast w-full border px-3 outline-none"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="type-label text-ink">Statement</span>
          <textarea
            value={sql}
            onChange={(event) => {
              setSql(event.target.value);
              // Its columns were the old statement's.
              setPreviewed(null);
            }}
            spellCheck={false}
            rows={8}
            placeholder="SELECT …"
            className="bg-surface border-border rounded-element type-code text-ink focus:border-border-emphasized motion-fast w-full resize-y border p-3 outline-none"
          />
        </label>

        {previewed ? (
          <>
            <PresentationFields
              draft={draft}
              columns={previewed.columns ?? []}
              onChange={setDraft}
            />
            <ArtifactCard artifact={previewed.artifact} csvUrl="" chrome={false} />
            <p className="type-support text-ink-secondary">
              Change how it is drawn and Preview again; Run and add saves what the form says.
            </p>
          </>
        ) : (
          <p className="type-support text-ink-secondary">
            Preview it to draw it as a chart or a map — or add it as a table and use the tile's
            Edit chart later. Either way it runs through the same guard as everything else.
          </p>
        )}
      </div>
    </Modal>
  );
}
